import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createTicket } from '@mkcore/portal/schema';
import { Button, Card, Field, Input, NativeSelect, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { useErrorMessage } from '@/lib/mutation';
import { FilePicker, uploadFile } from '@/features/portal/tickets';

/** Report an incident or request (CO-0660): a fixed form, the SLA of the engagement follows on our side. */
export const Route = createFileRoute('/portal/_client/tickets/new')({
  component: NewTicket,
});

const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

function NewTicket() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const [type, setType] = useState<'incident' | 'request'>('incident');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<(typeof PRIORITIES)[number]>('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const fileIds = [];
      for (const f of files) fileIds.push(await uploadFile(f));
      const created = await call(createTicket, { type, subject: subject.trim(), body: body.trim(), priority, fileIds });
      await qc.invalidateQueries({ queryKey: ['portal'] });
      await navigate({ to: '/portal/tickets/$id', params: { id: created.id } });
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };
  return (
    <>
      <h1 className="text-2xl font-bold">{t('portal.tickets.new')}</h1>
      <Card className="p-4">
        <form className="grid gap-4" onSubmit={(e) => void send(e)}>
          <fieldset className="grid gap-1">
            <legend className="mb-1 text-sm font-bold">{t('portal.tickets.type')}</legend>
            <div className="flex flex-wrap gap-4 text-sm">
              {(['incident', 'request'] as const).map((x) => (
                <label key={x} className="flex items-center gap-2">
                  <input type="radio" name="type" value={x} checked={type === x} onChange={() => setType(x)} /> {t(`portal.tickets.types.${x}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <Field label={t('portal.tickets.subject')}>{(p) => <Input {...p} required maxLength={300} value={subject} onChange={(e) => setSubject(e.target.value)} />}</Field>
          <Field label={t('portal.tickets.body')} hint={t('portal.tickets.bodyHint')}>
            {(p) => <Textarea {...p} required rows={8} value={body} onChange={(e) => setBody(e.target.value)} />}
          </Field>
          <Field label={t('portal.tickets.priority')}>
            {(p) => (
              <NativeSelect {...p} value={priority} onChange={(e) => setPriority(e.target.value as (typeof PRIORITIES)[number])}>
                {PRIORITIES.map((x) => (
                  <option key={x} value={x}>
                    {t(`portal.tickets.priorities.${x}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <FilePicker id="new-ticket-files" files={files} onChange={setFiles} />
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" loading={busy} disabled={!subject.trim() || !body.trim()}>
              {t('portal.tickets.send')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => void navigate({ to: '/portal/tickets' })}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
